import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../contexts/CartContext';
import { creditPackageService, membershipService } from '../../api/services.js';
import { solRoomTheme } from '../../themes/sol-room';
import AddToCartPopup from './AddToCartPopup';
import ShopSection from './ShopSection';
import { CartButton, CartPopup } from '../CartSystem';
import { ReactComponent as CartIcon } from '../../../assets/cart-icon.svg';

const Container = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.layoutGutter};
  overflow-x: hidden;
  width: 100%;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.layoutGutter};
    max-width: 100vw;
  }
`;

const NavigationBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: ${props => props.theme.spacing.sm};
    margin-bottom: ${props => props.theme.spacing.lg};
    justify-content: space-between;
  }
`;

const CartButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: static;
    transform: none;
    flex-shrink: 0;
  }
`;

const NavButton = styled.button`
  font-family: ${props => props.theme.typography.button.fontFamily};
  font-size: ${props => props.theme.typography.button.fontSize};
  font-weight: ${props => props.theme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme.typography.button.letterSpacing};
  text-transform: ${props => props.theme.typography.button.textTransform};
  
  background: ${props => props.$active ? props.theme.colors.primary.buttons : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.primary.background : props.theme.colors.primary.buttons};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    font-size: 0.85rem;
    flex: 1;
    text-align: center;
  }
`;

const Section = styled(motion.section)`
  margin-bottom: ${props => props.theme.spacing.xl};
  overflow: hidden;
  width: 100%;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SubsectionTitle = styled.h3`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 1.25rem;
  font-weight: 500;
  color: ${props => props.theme.colors.primary.text};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;
  opacity: 0.8;
`;

const CardRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
  overflow-x: auto;
  padding: ${props => props.theme.spacing.sm} 0;
  scroll-behavior: smooth;
  justify-content: center;
  
  width: 100%;
  max-width: 100%;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.neutral.light};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.accent.main};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.accent.deep};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: ${props => props.theme.spacing.md};
    justify-content: flex-start;
    max-width: calc(100vw - (${props => props.theme.spacing.layoutGutter} * 2));
    overflow-x: auto;
    overflow-y: hidden;
    &::-webkit-scrollbar {
      height: 4px;
    }
  }
`;

const Card = styled(motion.div)`
  background: ${props => props.theme.colors.neutral.lightest};
  border: ${props => props.$isPopular ? `2px solid ${props.theme.colors.accent.main}` : `1px solid ${props.theme.colors.accent.bright}`};
  border-radius: 12px;
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  transition: ${props => props.theme.transitions.default};
  width: 240px;
  flex-shrink: 0;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 40px ${props => props.theme.shadows.medium};
  }
`;


const CardTitle = styled.h4`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const CardDescription = styled.p`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.7;
  line-height: 1.4;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Price = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent.main};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SmallButton = styled.button`
  font-family: ${props => props.theme.typography.button.fontFamily};
  font-size: 0.85rem;
  font-weight: ${props => props.theme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme.typography.button.letterSpacing};
  text-transform: ${props => props.theme.typography.button.textTransform};
  
  background: transparent;
  color: ${props => props.theme.colors.primary.buttons};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: 10px 20px;
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  text-decoration: none;
  min-width: auto;
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.colors.primary.buttons};
  color: ${props => props.theme.colors.primary.background};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: 20px;
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CartIndicator = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: ${props => props.theme.colors.primary.buttons};
  color: ${props => props.theme.colors.primary.background};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: 20px;
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 0.75rem;
  font-weight: 600;
  border: 2px solid ${props => props.theme.colors.primary.background};
  z-index: 10;
  
  svg {
    width: 12px;
    height: 12px;
    fill: currentColor;
  }
`;

// Purchase options data
export const purchaseOptions = [
  {
    id: 'credits',
    name: 'Credits',
    title: 'Class Credits'
  },
  {
    id: 'memberships',
    name: 'Memberships',
    title: 'Memberships'
  },
  {
    id: 'shop',
    name: 'The Sol Shop',
    title: 'Sol Room Store'
  }
];


// Cache for API data to prevent multiple calls
let purchaseDataCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 10 * 1000; // 10 seconds

const PurchasePageContent = () => {
  const [activeTab, setActiveTab] = useState(purchaseOptions[0].id);
  const [purchasePopupOpen, setPurchasePopupOpen] = useState(false);
  const [cartPopupOpen, setCartPopupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const location = useLocation();
  const { cartItems } = useCart();
  const [direction, setDirection] = useState(0);
  
  // API data state
  const [creditsData, setCreditsData] = useState({});
  const [membershipsData, setMembershipsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get cart quantity for an item
  const getCartQuantity = (item, itemType, category = '') => {
    const cartId = category ? `${itemType}_${category}_${item.name}` : `${itemType}_${item.name}`;
    const cartItem = cartItems.find(cartItem => cartItem.id === cartId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Helper function to calculate monthly equivalent cost
  const calculateMonthlyCost = (price, periodType) => {
    switch (periodType) {
      case 'monthly':
        return price;
      case 'quarterly':
        return price / 3;
      case 'annually':
        return price / 12;
      default:
        return price;
    }
  };

  // Helper function to format billing period for description
  const getBillingPeriodText = (periodType) => {
    switch (periodType) {
      case 'monthly':
        return 'billed monthly';
      case 'quarterly':
        return 'billed quarterly';
      case 'annually':
        return 'billed annually';
      default:
        return 'billed monthly';
    }
  };

  // Load credits and memberships from API
  useEffect(() => {
    const loadPurchaseData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check cache first
        const now = Date.now();
        if (purchaseDataCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
          setCreditsData(purchaseDataCache.creditsData);
          setMembershipsData(purchaseDataCache.membershipsData);
          setLoading(false);
          return;
        }

        // Load credits and memberships in parallel
        const [creditPackages, memberships, purchasableEntities] = await Promise.all([
          creditPackageService.getCreditPackages(),
          membershipService.getMemberships(),
          creditPackageService.getPurchasableEntities()
        ]);

        // Extract data from API response structure
        const creditPackagesData = creditPackages.data || creditPackages || [];
        const membershipsData = memberships.data || memberships || [];
        const entitiesData = purchasableEntities.data || purchasableEntities || [];
        
        // Create a map of entity IDs to entity data for quick lookup
        const entityMap = {};
        entitiesData.forEach(entity => {
          entityMap[entity.id] = entity;
        });
        
        // Process credit packages by credit type, sorted by price
        const processedCredits = {};
        

        // Group credit packages by credit type (based on the credit they contain)
        for (const pkg of creditPackagesData) {
          // Get the entity data for this credit package
          const entity = entityMap[pkg.purchasable_entity_id];
          if (!entity) {
            console.warn('No entity found for credit package:', pkg.id);
            continue;
          }
          // Get the credit type from the entity name
          let creditType = 'other';
          let creditTypeName = 'Other';
          
          // Use the entity name to determine the category
          const entityName = entity.name?.toLowerCase() || '';
          
          if (entityName.includes('mat')) {
            creditType = 'group_mat';
            creditTypeName = 'Mat Classes';
          } else if (entityName.includes('reformer')) {
            creditType = 'group_reformer';
            creditTypeName = 'Reformer Classes';
          } else if (entityName.includes('private')) {
            creditType = 'private';
            creditTypeName = 'Private Sessions';
          }
          
          if (!processedCredits[creditType]) {
            processedCredits[creditType] = {
              title: creditTypeName,
              packages: []
            };
          }
          
          // Calculate total classes from items or extract from entity name/description
          const totalClasses = pkg.items 
            ? Object.values(pkg.items).reduce((sum, quantity) => sum + (quantity || 0), 0)
            : parseInt(entity.name?.match(/\d+/)?.[0]) || 1;
          
          processedCredits[creditType].packages.push({
            id: pkg.id,
            name: entity.name || 'Credit Package',
            classes: totalClasses,
            price: entity.price || 0,
            description: entity.short_description || '',
            fullDescription: entity.full_description || entity.short_description || '',
            isPopular: (entity.tags || []).includes('most-popular') || (entity.tags || []).includes('popular'),
            originalData: pkg,
            entityData: entity
          });
        }
        
        // Sort packages by price within each credit type
        Object.keys(processedCredits).forEach(type => {
          processedCredits[type].packages.sort((a, b) => a.price - b.price);
        });

        // Create ordered credits object: group_mat > group_reformer > private > other
        const orderedCredits = {};
        const desiredOrder = ['group_mat', 'group_reformer', 'private', 'other'];
        
        desiredOrder.forEach(creditType => {
          if (processedCredits[creditType]) {
            orderedCredits[creditType] = processedCredits[creditType];
          }
        });


        // Process memberships by event type
        const processedMemberships = {};
        
        for (const membership of membershipsData) {
          // Get the entity data for this membership
          const entity = entityMap[membership.purchasable_entity_id];
          if (!entity) {
            console.warn('No entity found for membership:', membership.id);
            continue;
          }
          // Use entity name to determine membership type (similar to credit packages)
          let membershipType = 'other';
          let membershipTypeName = 'Other Memberships';
          
          const entityName = entity.name?.toLowerCase() || '';
          
          if (entityName.includes('unlimited') || entityName.includes('group')) {
            membershipType = 'group';
            membershipTypeName = 'Group Class Memberships';
          } else if (entityName.includes('private')) {
            membershipType = 'private';
            membershipTypeName = 'Private Session Memberships';
          } else if (entityName.includes('wellness')) {
            membershipType = 'wellness';
            membershipTypeName = 'Wellness Memberships';
          }
          
          if (!processedMemberships[membershipType]) {
            processedMemberships[membershipType] = {
              title: membershipTypeName,
              packages: []
            };
          }
          
          // Add billing period to short description if not monthly
          const enhancedDescription = entity.period_type === 'monthly' 
            ? (entity.short_description || '')
            : `${entity.short_description || ''} • ${getBillingPeriodText(entity.period_type)}`;

          processedMemberships[membershipType].packages.push({
            id: membership.id,
            name: entity.name || 'Membership',
            price: entity.price || 0,
            monthlyCost: calculateMonthlyCost(entity.price || 0, entity.period_type || 'monthly'),
            description: enhancedDescription,
            fullDescription: entity.full_description || entity.short_description || '',
            periodType: entity.period_type || 'monthly',
            isPopular: (entity.tags || []).includes('most-popular') || (entity.tags || []).includes('popular'),
            originalData: membership,
            entityData: entity
          });
        }
        
        // Sort memberships by price within each type
        Object.keys(processedMemberships).forEach(type => {
          processedMemberships[type].packages.sort((a, b) => a.price - b.price);
        });

        // Create ordered memberships object: group > private > wellness > event > other
        const orderedMemberships = {};
        const membershipOrder = ['group', 'private', 'wellness', 'event', 'other'];
        
        membershipOrder.forEach(membershipType => {
          if (processedMemberships[membershipType]) {
            orderedMemberships[membershipType] = processedMemberships[membershipType];
          }
        });

        setCreditsData(orderedCredits);
        setMembershipsData(orderedMemberships);
        
        // Cache the processed data
        purchaseDataCache = {
          creditsData: orderedCredits,
          membershipsData: orderedMemberships
        };
        cacheTimestamp = Date.now();
        
      } catch (err) {
        console.error('Failed to load purchase data:', err);
        // Provide more specific error messages
        if (err.message?.includes('Network') || err.message?.includes('fetch')) {
          setError('Unable to connect to the server. Please check your connection and try again.');
        } else if (err.response?.status === 401) {
          setError('Your session has expired. Please log in again.');
        } else if (err.response?.status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(`Failed to load purchase options: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadPurchaseData();
  }, []);

  // Handle tab change with direction tracking
  const handleTabChange = (newTab) => {
    console.log('Tab change requested:', newTab);
    
    // Prevent redundant tab changes
    if (newTab === activeTab) {
      console.log('Same tab, ignoring');
      return;
    }
    
    const currentIndex = purchaseOptions.findIndex(option => option.id === activeTab);
    const newIndex = purchaseOptions.findIndex(option => option.id === newTab);
    
    if (newIndex > currentIndex) {
      setDirection(1); // Moving forward (left to right)
    } else if (newIndex < currentIndex) {
      setDirection(-1); // Moving backward (right to left)
    } else {
      setDirection(0); // Same tab
    }
    
    setActiveTab(newTab);
    
    // Update URL hash
    window.history.replaceState(null, '', `#${newTab}`);
    
    console.log('Tab changed to:', newTab);
  };

  // Handle initial hash on mount and hash changes from browser navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && purchaseOptions.find(option => option.id === hash)) {
      // Only update if different from current tab
      if (hash !== activeTab) {
        console.log('Hash navigation detected:', hash);
        const currentIndex = purchaseOptions.findIndex(option => option.id === activeTab);
        const newIndex = purchaseOptions.findIndex(option => option.id === hash);
        
        if (newIndex > currentIndex) {
          setDirection(1);
        } else if (newIndex < currentIndex) {
          setDirection(-1);
        } else {
          setDirection(0);
        }
        
        setActiveTab(hash);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const handlePurchase = (item, itemType, category = '') => {
    setSelectedItem({ ...item, category }); // Include category in the item
    setSelectedItemType(itemType);
    setPurchasePopupOpen(true);
  };

  const handleCartClick = () => {
    setCartPopupOpen(true);
  };

  const renderCreditsSection = () => (
    <Section
      key="credits"
      initial={{ opacity: 0, x: direction > 0 ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction > 0 ? -20 : 20 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
          >
      {Object.entries(creditsData).map(([type, category]) => (
        <div key={type}>
          <SubsectionTitle>{category.title}</SubsectionTitle>
          <CardRow>
            {category.packages.map((pkg, index) => (
              <Card
                key={index}
                whileHover={{ y: -4 }}
                $isPopular={pkg.isPopular}
                              >
                {pkg.isPopular && <PopularBadge>Popular</PopularBadge>}
                {getCartQuantity(pkg, 'credit', type) > 0 && (
                  <CartIndicator>
                    <CartIcon />
                    {getCartQuantity(pkg, 'credit', type)}
                  </CartIndicator>
                )}
                <CardTitle>{pkg.name}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
                <Price>${pkg.price}</Price>
                <SmallButton onClick={() => handlePurchase(pkg, 'credit', type)}>
                  Purchase
                </SmallButton>
              </Card>
            ))}
          </CardRow>
        </div>
      ))}
    </Section>
  );

  const renderMembershipsSection = () => (
    <Section
      key="memberships"
      initial={{ opacity: 0, x: direction > 0 ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction > 0 ? -20 : 20 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
          >
      {Object.entries(membershipsData).map(([type, category]) => (
        <div key={type}>
          <SubsectionTitle>{category.title}</SubsectionTitle>
          <CardRow>
            {category.packages.map((pkg, index) => (
              <Card
                key={index}
                whileHover={{ y: -4 }}
                $isPopular={pkg.isPopular}
                              >
                {pkg.isPopular && <PopularBadge>Popular</PopularBadge>}
                {getCartQuantity(pkg, 'membership', type) > 0 && (
                  <CartIndicator>
                    <CartIcon />
                    {getCartQuantity(pkg, 'membership', type)}
                  </CartIndicator>
                )}
                <CardTitle>{pkg.name}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
                <Price>${Math.round(pkg.monthlyCost)}/month</Price>
                <SmallButton onClick={() => handlePurchase(pkg, 'membership', type)}>
                  Subscribe
                </SmallButton>
              </Card>
            ))}
          </CardRow>
        </div>
      ))}
    </Section>
  );

  const renderStoreSection = () => (
    <Section
      key="shop"
      initial={{ opacity: 0, x: direction > 0 ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction > 0 ? -20 : 20 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <ShopSection />
    </Section>
  );

  const renderActiveSection = () => {
    if (loading) {
      return (
        <Section
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading purchase options...</p>
          </div>
        </Section>
      );
    }

    if (error) {
      return (
        <Section
          key="error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: solRoomTheme.colors.accent.main }}>{error}</p>
          </div>
        </Section>
      );
    }

    switch (activeTab) {
      case 'credits':
        return renderCreditsSection();
      case 'memberships':
        return renderMembershipsSection();
      case 'shop':
        return renderStoreSection();
      default:
        return renderCreditsSection();
    }
  };

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
          >
      <NavigationBar>
        {purchaseOptions.map((option) => (
          <NavButton
            key={option.id}
            $active={activeTab === option.id}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Button clicked!', option.id);
              handleTabChange(option.id);
            }}
                      >
            {option.name}
          </NavButton>
        ))}
        <CartButtonContainer>
          <CartButton onClick={handleCartClick} />
        </CartButtonContainer>
      </NavigationBar>

      <AnimatePresence mode="wait">
        {renderActiveSection()}
      </AnimatePresence>
      
      <AddToCartPopup 
        isOpen={purchasePopupOpen}
        onClose={() => setPurchasePopupOpen(false)}
        item={selectedItem}
        itemType={selectedItemType}
      />
      
      <CartPopup 
        isOpen={cartPopupOpen}
        onClose={() => setCartPopupOpen(false)}
              />
    </Container>
  );
};

export default PurchasePageContent;